import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { GetEmployeeName } from "../../../services/authService";
import Cookies from "js-cookie";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getFunctions,
  getGrades,
  getZones,
  getDepartments,
  getJobLocations,
  getRoleType,
  getJobCategory,
} from "../../../services/metadataService";
import { Modal, Button } from "react-bootstrap";
import "./MasterDataTable.css";
import {
  addFunction,
  addGrade,
  addZone,
  addRoleType,
  addJobCategory,
  addDepartment,
  addJobLocation,
} from "../../../services/masterdataService";
import NormalAlert from "../../AlertComponent/NormalAlert";

const MasterDataTable = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [apiMessage, setApiMessage] = useState(null);
  const [dropdownList, setDropdownList] = useState([]);
  const [selectedParent, setSelectedParent] = useState("");
  const [alertData, setAlertData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      let res = [];

      switch (type.toLowerCase()) {
        case "functions":
          res = await getFunctions();
          break;

        case "grades":
          res = await getGrades();
          break;

        case "zones":
          res = await getZones();
          break;

        case "roletype":
        case "role-type":
        case "role-types":
          res = await getRoleType();
          break;

        case "jobcategory":
        case "job-category":
        case "job-categories":
          res = await getJobCategory();
          break;

        case "departments":
          const functions = await getFunctions();
          const deptResults = [];
          for (const func of functions) {
            const departments = await getDepartments(func.functionID);
            deptResults.push({
              parent: func.functionName,
              children: departments.map((d) => d.departmentName),
            });
          }
          res = deptResults;
          break;

        case "joblocations":
        case "job-location":
        case "job-locations":
          const zones = await getZones();
          const locResults = [];
          for (const z of zones) {
            const locations = await getJobLocations(z.zoneID);
            locResults.push({
              parent: z.zoneName,
              children: locations.map((l) => l.jobLocation),
            });
          }
          res = locResults;
          break;

        default:
          res = [];
      }

      setData(res);
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlertData({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [type]);

  useEffect(() => {
    const checkRole = async () => {
      const token = Cookies.get("employeeToken");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        const userRole = response?.data?.employeeRole;

        if (userRole !== "Admin Employee") {
          navigate("/Dashboard");
        }
      } catch (err) {
        navigate("/Dashboard");
      }
    };

    checkRole();
  }, []);

  const handleAdd = async () => {
    setAddValue("");
    setSelectedParent("");
    setApiMessage(null);

    const t = type.toLowerCase();

    if (t === "departments") {
      const funcs = await getFunctions();
      setDropdownList(funcs);
    }

    if (t === "joblocations" || t === "job-location" || t === "job-locations") {
      const zones = await getZones();
      setDropdownList(zones);
    }

    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!addValue.trim()) {
      setAlertData({
        type: "error",
        message: "Please enter a value.",
      });
      return;
    }

    try {
      let response;
      const t = type.toLowerCase();

      switch (t) {
        case "functions":
          response = await addFunction(addValue);
          break;

        case "grades":
          response = await addGrade(addValue);
          break;

        case "zones":
          response = await addZone(addValue);
          break;

        case "roletype":
        case "role-type":
        case "role-types":
          response = await addRoleType(addValue);
          break;

        case "jobcategory":
        case "job-category":
        case "job-categories":
          response = await addJobCategory(addValue);
          break;

        case "departments":
          if (!selectedParent) {
            setAlertData({
              type: "error",
              message: "Please select a Function.",
            });
            return;
          }
          response = await addDepartment(selectedParent, addValue);
          break;

        case "joblocations":
        case "job-location":
        case "job-locations":
          if (!selectedParent) {
            setAlertData({
              type: "error",
              message: "Please select a Zone.",
            });
            return;
          }
          response = await addJobLocation(selectedParent, addValue);
          break;

        default:
          setAlertData({
            type: "error",
            message: "Add option not implemented.",
          });
          return;
      }

      if (response?.data?.status === "Success") {
        // SUCCESS → Close modal and show alert
        setShowModal(false);

        setAlertData({
          type: "success",
          message: response.data.message,
        });

        fetchData(); // refresh table
      } else {
        // FAIL → Show alert inside alert box
        setAlertData({
          type: "error",
          message: response?.data?.message || "Something went wrong",
        });
      }
    } catch (err) {
      setAlertData({
        type: "error",
        message: err.response?.data?.message || "Something went wrong",
      });
      setShowModal(false);
    }
  };

  const filteredData = data.filter((item) => {
    if (
      item.functionName ||
      item.gradeName ||
      item.zoneName ||
      item.roleType ||
      item.jobCategory
    ) {
      const name =
        item.functionName ||
        item.gradeName ||
        item.zoneName ||
        item.roleType ||
        item.jobCategory;

      return name.toLowerCase().includes(searchTerm.toLowerCase());
    }

    if (item.parent) {
      return (
        item.parent.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.children.some((c) =>
          c.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return false;
  });

  const displayTitle =
    type.charAt(0).toUpperCase() + type.slice(1).replace("-", " ");

  return (
    <>
      <div className="jobs-container mt-5 px-3">
        <div className="jobs-card shadow-sm mt-5">
          {/* HEADER */}
          <div className="jobs-header d-flex justify-content-between align-items-center">
            <h3 className="fw-bold mb-0">{displayTitle} Master Data</h3>

            <Button
              variant="light"
              className="fw-semibold rounded-pill px-4 d-flex align-items-center text-dark"
              onClick={() => navigate("/Dashboard/Master-Data")}
            >
              <i className="bi bi-arrow-left me-2"></i> Back
            </Button>
          </div>

          {/* TOOLBAR */}
          <div className="jobs-toolbar d-flex flex-wrap align-items-center gap-3 my-3 w-100">
            <div
              className="input-group rounded-pill border bg-white shadow-sm"
              style={{ maxWidth: "350px" }}
            >
              <span className="input-group-text bg-transparent border-0 ps-3">
                <i className="bi bi-search text-secondary"></i>
              </span>
              <input
                type="text"
                className="form-control border-0 rounded-5 shadow-none"
                placeholder={`Search ${displayTitle.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <Button
              variant="primary"
              className="fw-semibold rounded-pill px-4 ms-auto d-flex align-items-center"
              onClick={handleAdd}
            >
              <i className="bi bi-plus-circle me-2"></i> Add {displayTitle}
            </Button>
          </div>

          {/* TABLE */}
          <div className="Overflow-Auto">
            {loading ? (
              <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
                <table className="table modern-table text-center align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>
                        {type.toLowerCase() === "departments"
                          ? "Function Name"
                          : type.toLowerCase() === "job-locations"
                          ? "Zone Name"
                          : `${displayTitle} Name`}
                      </th>

                      {(type.toLowerCase() === "departments" ||
                        type.toLowerCase() === "job-locations") && (
                        <th>
                          {type.toLowerCase() === "departments"
                            ? "Departments"
                            : "Job Locations"}
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {[...Array(5)].map((_, idx) => (
                      <tr key={idx}>
                        {/* # column */}
                        <td>
                          <Skeleton height={20} />
                        </td>

                        {/* Parent column */}
                        <td>
                          <Skeleton height={20} />
                        </td>

                        {/* Children column */}
                        {(type.toLowerCase() === "departments" ||
                          type.toLowerCase() === "job-locations") && (
                          <td>
                            <div className="d-flex gap-2 flex-wrap justify-content-center">
                              <Skeleton height={20} width={80} />
                              <Skeleton height={20} width={100} />
                              <Skeleton height={20} width={60} />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SkeletonTheme>
            ) : (
              <table className="table modern-table text-center align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>
                      {type.toLowerCase() === "departments"
                        ? "Function Name"
                        : type.toLowerCase() === "job-locations"
                        ? "Zone Name"
                        : `${displayTitle} Name`}
                    </th>

                    {(type.toLowerCase() === "departments" ||
                      type.toLowerCase() === "job-locations") && (
                      <th>
                        {type.toLowerCase() === "departments"
                          ? "Departments"
                          : "Job Locations"}
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) =>
                      type.toLowerCase() === "departments" ||
                      type.toLowerCase() === "job-locations" ? (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.parent}</td>
                          <td>
                            {item.children.length > 0 ? (
                              <div className="d-flex gap-2 flex-wrap justify-content-center">
                                {item.children.map((child, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-light text-dark border rounded-pill"
                                  >
                                    {child}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ) : (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            {item.functionName ||
                              item.gradeName ||
                              item.zoneName ||
                              item.roleType ||
                              item.jobCategory}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-danger text-center py-3">
                        No {displayTitle.toLowerCase()} found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ADD MODAL */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add {displayTitle}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Dropdown for Departments */}
            {type.toLowerCase() === "departments" && (
              <div className="mb-3">
                <label>Select Function</label>
                <select
                  className="form-select"
                  value={selectedParent}
                  onChange={(e) => setSelectedParent(e.target.value)}
                >
                  <option value="">-- Select Function --</option>
                  {dropdownList.map((f) => (
                    <option key={f.functionID} value={f.functionID}>
                      {f.functionName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Dropdown for Job Locations */}
            {(type.toLowerCase() === "joblocations" ||
              type.toLowerCase() === "job-location" ||
              type.toLowerCase() === "job-locations") && (
              <div className="mb-3">
                <label>Select Zone</label>
                <select
                  className="form-select"
                  value={selectedParent}
                  onChange={(e) => setSelectedParent(e.target.value)}
                >
                  <option value="">-- Select Zone --</option>
                  {dropdownList.map((z) => (
                    <option key={z.zoneID} value={z.zoneID}>
                      {z.zoneName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Name Input */}
            <div className="mb-3">
              <label>{displayTitle} Name</label>
              <input
                type="text"
                className="form-control"
                placeholder={`Enter ${displayTitle}`}
                value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
              />
            </div>

            {apiMessage && (
              <div
                className={`mt-2 ${
                  apiMessage.status === "Success"
                    ? "text-success"
                    : "text-danger"
                }`}
              >
                {apiMessage.message}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Add {displayTitle}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {alertData && (
        <NormalAlert
          type={alertData.type}
          message={alertData.message}
          autoClose={2500}
          onClose={() => setAlertData(null)}
        />
      )}
    </>
  );
};

export default MasterDataTable;
